"use client";

import { CMSLink } from "@/Link";
import type { ResolvedCMSLink } from "@/blocks/resolve";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import RichText from "@/components/RichText";
import { LogoMarqueeRow } from "@/components/pro-blocks/landing-page/logo-sections/logo-marquee-row";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";
import { ArrowRight } from "lucide-react";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";

type LogoItem = {
	src: string;
	alt?: string;
	multiplier?: number;
	objectFit?: MediaObjectFit;
};

type LogoRow = {
	id: string;
	speed?: number;
	direction?: "left" | "right";
	logos: LogoItem[];
};

type LogoSectionMultiRowProps = {
	tagline?: string;
	title?: string;
	subtitle?: string;
	titleTag?: "h1" | "h2" | "h3";
	titleClassName?: string;
	richtext?: DefaultTypedEditorState;
	link?: ResolvedCMSLink;
	rows: LogoRow[];
};

export function LogoSectionMultiRow({
	tagline,
	title,
	subtitle,
	titleTag = "h2",
	titleClassName = "heading-lg",
	richtext,
	link,
	rows,
}: LogoSectionMultiRowProps) {
	const TitleTag = titleTag;
	const validRows = rows.filter((row) => row.logos.length > 0);

	if (!validRows.length) return null;

	return (
		<section className="bg-background pt-12 pb-16">
			<div className="flex flex-col items-center gap-12 md:gap-16">
				{tagline || title || subtitle ? (
					<div className="container-padding-x mx-auto section-title-gap-lg flex w-full max-w-md sm:max-w-xl lg:max-w-2xl 3xl:max-w-3xl flex-col items-center text-center">
						{tagline ? <Tagline>{tagline}</Tagline> : null}
						{title ? (
							<TitleTag
								className={`${titleClassName} text-foreground w-full text-balance`}
							>
								{title}
							</TitleTag>
						) : null}
						{subtitle ? (
							<p className={`${titleClassName} text-muted-foreground w-full`}>
								{subtitle}
							</p>
						) : null}
						{richtext ? (
							<RichText
								data={richtext}
								enableGutter={false}
								enableProse={false}
								className="text-muted-foreground text-base text-pretty"
							/>
						) : null}
						{link ? (
							<CMSLink
								{...link}
								appearance={link.appearance ?? "default"}
								size="xl"
								className="group mt-2"
							>
								<ArrowRight className="transition-transform duration-200 group-hover:translate-x-1 group-hover/button:translate-x-1" />
							</CMSLink>
						) : null}
					</div>
				) : null}

				<div className="flex w-full flex-col gap-4 md:gap-6">
					{validRows.map((row) => (
						<LogoMarqueeRow
							key={row.id}
							rowId={row.id}
							direction={row.direction}
							speed={row.speed}
							logos={row.logos}
						/>
					))}
				</div>
			</div>
		</section>
	);
}
